import React from 'react';
import { Footer } from '../components/Footer';
import Icon from '../components/icons';
import Header from '../components/Header';

const Security: React.FC = () => {

  const securityFeatures = [
    {
      icon: "VerifiedUser",
      title: "HIPAA Pre-Certified Infrastructure",
      description: "We leverage AWS and Google Cloud HIPAA-compliant infrastructure, including encrypted databases, secure storage solutions, and protected networking components."
    },
    {
      icon: "Lock",
      title: "End-to-End Encryption",
      description: "All data transmitted between your device and our servers is protected with industry-standard TLS 1.3 encryption. Patient data at rest is encrypted using AES-256 algorithm."
    },
    {
      icon: "Shield",
      title: "Patient Data Protection",
      description: "We implement automatic de-identification of patient data, role-based access controls, and complete audit trails to protect sensitive medical information."
    },
    {
      icon: "Security",
      title: "Regular Security Audits",
      description: "We conduct regular penetration testing, vulnerability assessments, and security audits by independent third-party security experts."
    },
    {
      icon: "PrivacyTip",
      title: "Modular Compliance Approach",
      description: "We built our platform with a modular compliance approach, starting with robust data security and HIPAA/GDPR compliance, with a clear roadmap for additional certifications."
    },
    {
      icon: "CloudDone",
      title: "Compliant Deployment Options",
      description: "For healthcare organizations requiring specific compliance needs, we offer flexible deployment options that maintain all security protections while meeting your requirements."
    }
  ];

  const certifications = [
    { name: "HIPAA Compliance", description: "Healthcare privacy standards (active)" },
    { name: "SOC 2 Type II", description: "Security, availability, and confidentiality (via infrastructure partners)" },
    { name: "ISO 27001", description: "Information security management (roadmap Q4 2024)" },
    { name: "GDPR Compliance", description: "European data protection standards (active)" },
    { name: "CSA STAR", description: "Cloud security assurance (roadmap 2025)" }
  ];

  return (
    <div className="bg-white min-h-screen">
      {/* Shared Header Component */}
      <Header />
      
      {/* Content with padding for fixed header */}
      <div className="pt-16">
        {/* Hero section */}
        <div className="relative bg-gradient-to-r from-indigo-600 to-teal-600 overflow-hidden">
          <div className="absolute inset-0">
            <img
              className="w-full h-full object-cover mix-blend-multiply filter brightness-50"
              src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2000&q=80"
              alt="Security and encryption visual"
            />
          </div>
          <div className="relative max-w-7xl mx-auto py-24 px-4 sm:py-32 sm:px-6 lg:px-8">
            <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">Security & Compliance</h1>
            <p className="mt-6 max-w-3xl text-xl text-indigo-100">
              Protecting patient data is our highest priority. Our platform is built on secure infrastructure with comprehensive safeguards to ensure data privacy and security.
            </p>
          </div>
        </div>

        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto mb-12">
            <p className="text-lg text-gray-700 mb-6">
              At AgilisCare, protecting patient data and ensuring the security of our platform is our highest priority. Our comprehensive security program is designed to meet and exceed industry standards for healthcare data protection.
            </p>
            
            <p className="text-lg text-gray-700 mb-6">
              We understand that healthcare organizations face strict regulatory requirements, which is why we've built our platform on pre-certified infrastructure and implemented a modular compliance approach that starts with robust data security and privacy.
            </p>
          </div>

          <div className="bg-indigo-50 p-6 rounded-lg mb-16 max-w-4xl mx-auto">
            <h3 className="text-xl font-semibold mb-4 text-indigo-800">Our Modular Compliance Approach</h3>
            <p className="text-gray-700 mb-4">
              Rather than attempting to address all compliance requirements at once, we've adopted a strategic, phased approach:
            </p>
            <ol className="list-decimal pl-6 space-y-3">
              <li className="text-gray-700">
                <strong>Foundation:</strong> Robust data security practices and HIPAA compliance using pre-certified infrastructure
              </li>
              <li className="text-gray-700">
                <strong>Current Focus:</strong> Enhancing user data protection while improving clinical workflow efficiency
              </li>
              <li className="text-gray-700">
                <strong>Roadmap:</strong> Expanding certifications and compliance as we scale, with clear timelines for ISO 27001 and beyond
              </li>
            </ol>
          </div>

          <h2 className="text-2xl font-semibold mb-6 text-center">Security Features</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {securityFeatures.map((feature, index) => (
              <div key={index} className="bg-indigo-50 rounded-lg p-6 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="bg-indigo-600 p-3 rounded-full mr-4">
                    <Icon iconName={feature.icon} size={24} className="text-white" />
                  </div>
                  <h3 className="font-semibold text-xl text-gray-800">{feature.title}</h3>
                </div>
                <p className="text-gray-700">{feature.description}</p>
              </div>
            ))}
          </div>

          <h2 className="text-2xl font-semibold mb-6 text-center">Certifications & Compliance Roadmap</h2>
          
          <div className="max-w-3xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
            {certifications.map((cert, index) => (
              <div key={index} className="flex items-center bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="bg-green-600 h-10 w-10 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <Icon iconName="CheckCircle" size={24} className="text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800">{cert.name}</h3>
                  <p className="text-gray-600 text-sm">{cert.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="max-w-4xl mx-auto bg-indigo-100 rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-semibold mb-4 text-center">Our Security Commitment</h2>
            <p className="text-gray-700 mb-4">
              We are committed to maintaining the highest standards of security for our platform. Our dedicated security team continuously monitors for potential threats and vulnerabilities, and we regularly update our security protocols to address emerging risks.
            </p>
            <p className="text-gray-700">
              For more detailed information about our security practices or to request our security whitepaper, please contact our security team at security@agiliscare.com.
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Security; 